import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { CustomerServiceLoanCategory, getCustomerServicePhone, getCustomerServiceTel } from '@ld.consumer-portal/constants';
import { ShareScreenService } from '@ld.consumer-portal/data-access';
import {
  heroiconScreenshare,
  heroiconsAnnotation,
  heroiconsContactMail,
  heroiconsContactPhone,
  supportIcon,
} from '@ld.consumer-portal/data-uri';
import { LoanSelectionService, PlatformService, ServisBotService } from '@ld.consumer-portal/utils';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ld-contact-card',
  imports: [SvgIconComponent, TranslocoDirective, MatProgressSpinnerModule],
  templateUrl: './contact-card.component.html',
  styleUrl: './contact-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactCardComponent {
  private readonly router = inject(Router);
  private readonly servisBotService = inject(ServisBotService);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly platformService = inject(PlatformService);
  private readonly shareScreenService = inject(ShareScreenService);

  protected readonly isNativeMobile = this.platformService.isNativeMobile;

  loanType = input<string | undefined>(undefined);

  private readonly effectiveLoanType = computed(
    () => this.loanType() ?? this.loanSelectionService.selectedLoanType() ?? 'MORTGAGE',
  );

  private readonly effectiveLoanCategory = computed((): CustomerServiceLoanCategory => {
    const loanType = this.effectiveLoanType();
    if (loanType === 'HELOC') return 'HELOC';
    if (this.loanSelectionService.isVaLoan()) return 'VA';
    return 'MORTGAGE';
  });

  protected readonly phoneDisplay = computed(() => getCustomerServicePhone(this.effectiveLoanCategory()));
  protected readonly phoneTel = computed(() => getCustomerServiceTel(this.effectiveLoanCategory()));
  protected readonly supportIcon = supportIcon;
  protected readonly annotationIcon = heroiconsAnnotation;
  protected readonly contactMailIcon = heroiconsContactMail;
  protected readonly contactPhoneIcon = heroiconsContactPhone;
  protected readonly screenshareIcon = heroiconScreenshare;
  protected readonly isShareScreenLoading = this.shareScreenService.isStarting;

  protected onShareScreenClick(): Promise<void> {
    return this.shareScreenService.startSession('ContactCardComponent');
  }

  protected openChat(): void {
    this.servisBotService.open();
  }

  protected openMessageCenter(): void {
    const accountId = this.loanSelectionService.selectedLoanNumber();
    if (accountId) {
      this.router.navigate(['/messages', accountId]);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }
}
